import addDays from "date-fns/addDays";
import addMinutes from "date-fns/addMinutes";
import addMonths from "date-fns/addMonths";
import addWeeks from "date-fns/addWeeks";
import addYears from "date-fns/addYears";
import differenceInMinutes from "date-fns/differenceInMinutes";
import endOfDay from "date-fns/endOfDay";
import endOfWeek from "date-fns/endOfWeek";
import format from "date-fns/format";
import getDate from "date-fns/getDate";
import getHours from "date-fns/getHours";
import getMinutes from "date-fns/getMinutes";
import getMonth from "date-fns/getMonth";
import getSeconds from "date-fns/getSeconds";
import getWeekOfMonth from "date-fns/getWeekOfMonth";
import getYear from "date-fns/getYear";
import isSameDay from "date-fns/isSameDay";
import isSameMonth from "date-fns/isSameMonth";
import isValid from "date-fns/isValid";
import isWeekend from "date-fns/isWeekend";
import parse from "date-fns/parse";
import setHours from "date-fns/setHours";
import setMinutes from "date-fns/setMinutes";
import setSeconds from "date-fns/setSeconds";
import startOfDay from "date-fns/startOfDay";
import startOfMonth from "date-fns/startOfMonth";
import startOfWeek from "date-fns/startOfWeek";
import subDays from "date-fns/subDays";
import subMonths from "date-fns/subMonths";
import subWeeks from "date-fns/subWeeks";
import subYears from "date-fns/subYears";
import React, { ChangeEvent, FocusEvent, FocusEventHandler, useCallback, useMemo, useReducer, useRef } from "react";
import { DAYS, MONTHS, TIME_CHANGE_DELAY } from "../../components/constants";
import { DateRange } from "../../components/types";
import {
  getRangeInformation,
  handleRefs,
  InvalidDateType,
  isDayDisabled,
  isValidDate,
  isWeekInMonth,
  isWithinTime,
  monthDisabledAfter,
  monthDisabledBefore,
  normalizeArrowKey,
  yearDisabledAfter,
  yearDisabledBefore,
} from "../../utils/date-utils";
import { useDebounce } from "../use-debounce";
import { useElementId } from "../use-element-id";
import {
  setCalendarOpenState,
  setInputValue,
  setNextMonth,
  setNextYear,
  setPreSelectionDate,
  setPreviousMonth,
  setPreviousYear,
  setRangeHoverDay,
} from "./actions";
import { calendarInitialState as calendarInitialStateData, calendarReducer } from "./reducer";

const DATE_INTERNAL_FORMAT = "yyyy-MM-dd";
const TOTAL_MINUTES_IN_DAY = 1440;

export type WeekendFn = (day: Number) => boolean;

export interface UseCalendarProps {
  /**
   * This props controls the selected date
   */
  selectedDate: Date | null;

  /**
   * This props controls date format, use valid date format from date-fns library
   */
  dateFormat?: string;

  /**
   * This event handler will be called when valid date is entered in input or selected from the date picker
   */
  onDateSelect: (date: Date | null) => void;

  /**
   * This prop controls the no. of month to show. For mobile view it is fixed to 1 and for desktop it's default value is 2
   * @default 2
   */
  showMonths?: number;

  /**
   * This props controls the minimum date to show
   */
  minDate?: Date;

  /**
   * This props controls the maximum date to show
   */
  maxDate?: Date;

  /**
   * This props controls the dates to exclude
   */
  excludeDates?: Date[];

  /**
   * This props controls the dates to include
   */
  includeDates?: Date[];

  /**
   * This props indicates that calendar should select start date in range
   */
  selectsStart?: boolean;

  /**
   * This props indicates that calendar should select end date in range
   */
  selectsEnd?: boolean;

  /**
   * This prop controls the selected start date of a range picker
   */
  startDate?: Date;

  /**
   * This prop controls the selected end date of a range picker
   */
  endDate?: Date;

  /**
   * This function will be called when populating the day. it should return true if day is weekend else false.
   */
  isWeekEnd?: WeekendFn;

  /**
   * This prop controls whether calender should show time.
   */
  withTime?: boolean;

  /**
   * This prop controls whether calender should only time.
   */
  onlyTime?: boolean;

  /**
   * This prop controls time format for the time.
   */
  timeFormat?: string;

  /**
   * This prop controls time interval, it is used when generating the time picker from timer intervals.
   */
  timeIntervals?: number;

  /**
   * This prop controls min Time.
   */
  minTime?: Date;

  /**
   * This prop controls max Time.
   */
  maxTime?: Date;

  /**
   * callback, This event handler will be called when invalid date is entered(keyed) in input with respect to minDate, maxDate, excludeDates and includeDates
   */
  onInvalidDate?: (error: InvalidDateType, date: Date | null) => void;
}

export interface GetInputPropsOptions extends React.HTMLProps<HTMLInputElement> {
  disabled?: boolean;
  onFocus?: FocusEventHandler<HTMLInputElement>;
}

export interface GetButtonPropsOptions extends React.HTMLProps<HTMLButtonElement> {}

export interface Calendar {
  isOpen: boolean;
  months: Month[];
  times: Date[];

  /** Getters */
  getInputProps: (options?: GetInputPropsOptions) => any;
  getCalendarRootProps: (options?: React.HTMLProps<HTMLDivElement>) => any;
  getGridProps: (options?: React.HTMLProps<HTMLTableElement>) => any;
  getPreviousYearProps: (options?: GetButtonPropsOptions) => any;
  getPreviousMonthProps: (options?: GetButtonPropsOptions) => any;
  getMonthTitleProps: (options?: React.HTMLProps<HTMLHeadingElement>) => any;
  getNextMonthProps: (options?: GetButtonPropsOptions) => any;
  getNextYearProps: (options?: GetButtonPropsOptions) => any;
  getDayProps: (day: Day, options?: GetButtonPropsOptions) => any;

  /**Actions */
  nextMonth: () => void;
  prevMonth: () => void;
  nextYear: () => void;
  prevYear: () => void;
  openCalendar: () => void;
  closeCalendar: () => void;
  selectTime: (time: Date | null) => void;
}

export interface Time {
  date: Date;
  hours: number;
  minutes: number;
  seconds: number;
  formattedTime: string;
  selected: boolean;
}

export interface Day extends DateRange {
  date: Date;
  dayNumber: number;
  dayName: string;
  selected: boolean;
  preSelected: boolean;
  belongToMonth: boolean;
  disabled: boolean;
  weekend: boolean;
}

export interface Week {
  weekNumber: number;
  days: Day[];
}

export interface Month {
  monthName: string;
  monthNumber: number;
  year: number;
  weeks: Week[];
}

export const useCalendar = ({
  selectedDate,
  onDateSelect,
  onInvalidDate,
  dateFormat = "MM/dd/yyyy",
  showMonths = 2,
  minDate,
  maxDate,
  excludeDates,
  includeDates,
  selectsStart = false,
  selectsEnd = false,
  isWeekEnd,
  startDate,
  endDate,
  withTime = false,
  onlyTime = false,
  timeFormat = "p",
  timeIntervals,
  minTime,
  maxTime,
}: UseCalendarProps): Calendar => {
  const calendarInitialState = { ...calendarInitialStateData };
  if (selectedDate) {
    calendarInitialState.preSelectionDate = selectedDate;
  }
  const [state, dispatch] = useReducer(calendarReducer, calendarInitialState);
  const dialogLabelId = useElementId(undefined, "calendar");
  const daysRef = useRef<{ [key: string]: any }>({});
  const inputRef = useRef<HTMLInputElement>();
  const debounceFn = useDebounce();

  const openCalendar = useCallback(() => {
    dispatch(setCalendarOpenState(true));
    if (selectedDate) {
      dispatch(setPreSelectionDate(selectedDate as Date));
    } else {
      dispatch(setPreSelectionDate(new Date()));
    }
  }, [selectedDate]);

  const closeCalendar = useCallback(() => {
    if (selectedDate) {
      dispatch(setPreSelectionDate(selectedDate as Date));
    }
    dispatch(setCalendarOpenState(false));
  }, [selectedDate]);

  const getMonths = useCallback(
    (
      date: Date,
      selectedDate: Date | null,
      preSelectedDate: Date,
      rangeHoverDay: Date | null,
      minDate?: Date,
      maxDate?: Date,
      excludeDates?: Date[],
      includeDates?: Date[],
      startDate?: Date,
      endDate?: Date,
      isWeekEnd?: WeekendFn,
      selectsStart: boolean = false,
      selectsEnd: boolean = false
    ): Month => {
      const monthNumber = getMonth(date);

      const weeks: Week[] = [];
      const monthStartDate = startOfMonth(date);
      let currentWeekStart = startOfWeek(monthStartDate);
      do {
        const days: Day[] = [];
        for (let i = 0; i < 7; i++) {
          const dayDate = addDays(currentWeekStart, i);
          const dayNumber = getDate(dayDate);
          const disabled = isDayDisabled(
            dayDate,
            minDate as Date,
            maxDate as Date,
            excludeDates as Date[],
            includeDates as Date[]
          );
          const day: Day = {
            date: dayDate,
            dayNumber,
            dayName: DAYS[i],
            weekend: isWeekEnd ? isWeekend(dayNumber) : false,
            selected: !!selectedDate && isSameDay(dayDate, selectedDate) && isSameMonth(dayDate, monthStartDate),
            preSelected: isSameDay(dayDate, preSelectedDate),
            belongToMonth: isSameMonth(dayDate, monthStartDate),
            disabled,
            ...getRangeInformation(
              dayDate,
              rangeHoverDay,
              startDate as Date,
              endDate as Date,
              selectsStart,
              selectsEnd,
              disabled
            ),
          };
          days.push(day);
        }

        const weekNumber = isSameMonth(date, currentWeekStart)
          ? getWeekOfMonth(currentWeekStart)
          : getWeekOfMonth(monthStartDate);
        const week: Week = { weekNumber, days };
        weeks.push(week);
        currentWeekStart = addWeeks(currentWeekStart, 1);
      } while (isWeekInMonth(date, currentWeekStart));

      const month: Month = {
        monthNumber,
        monthName: MONTHS[monthNumber],
        year: getYear(date),
        weeks: weeks,
      };

      return month;
    },
    []
  );

  const months = useMemo(() => {
    const months: Month[] = [];
    if (!state.open) {
      return months;
    }

    for (let i = 0; i < showMonths; i++) {
      const date = addMonths(state.preSelectionDate, i);
      months.push(
        getMonths(
          date,
          selectedDate,
          state.preSelectionDate,
          state.rangeHoverDay,
          minDate,
          maxDate,
          excludeDates,
          includeDates,
          startDate,
          endDate,
          isWeekEnd,
          selectsStart,
          selectsEnd
        )
      );
    }

    return months;
  }, [
    endDate,
    excludeDates,
    getMonths,
    includeDates,
    isWeekEnd,
    maxDate,
    minDate,
    selectedDate,
    selectsEnd,
    selectsStart,
    showMonths,
    startDate,
    state.open,
    state.preSelectionDate,
    state.rangeHoverDay,
  ]);

  const times = useMemo(() => {
    const times: Date[] = [];
    if (!(withTime || onlyTime)) {
      return times;
    }

    const baseDate = minTime
      ? minTime
      : onlyTime || !selectedDate
      ? startOfDay(new Date())
      : startOfDay(selectedDate as Date);
    const intervals = timeIntervals ? timeIntervals : 1;
    const totalMinutes = minTime ? differenceInMinutes(minTime, endOfDay(minTime)) : TOTAL_MINUTES_IN_DAY;
    const noOfIntervals = totalMinutes / intervals;
    for (let i = 0; i < noOfIntervals; i++) {
      const currentTime = addMinutes(baseDate, i * intervals);
      if (maxTime && !isWithinTime(currentTime, baseDate, maxTime)) {
        break;
      }
      times.push(currentTime);
    }
    return times;
  }, [maxTime, minTime, onlyTime, selectedDate, timeIntervals, withTime]);

  const getSelectedDateWithTime = useCallback((selectedDate, newDate) => {
    let seconds = 0;
    let minutes = 0;
    let hours = 0;
    if (selectedDate) {
      seconds = getSeconds(selectedDate);
      minutes = getMinutes(selectedDate);
      hours = getHours(selectedDate);
    }
    return setHours(setMinutes(setSeconds(newDate, seconds), minutes), hours);
  }, []);

  const onDateSelectHandler = useCallback(() => {
    dispatch(setCalendarOpenState(false));
    dispatch(setPreSelectionDate(state.preSelectionDate));
    dispatch(setRangeHoverDay(null));
    let selectedDateTime = state.preSelectionDate;
    if (withTime) {
      selectedDateTime = getSelectedDateWithTime(selectedDate, selectedDateTime);
    }

    const dateInputStatus = isValidDate(selectedDateTime, minDate, maxDate, includeDates, excludeDates);
    if (dateInputStatus === true) {
      onDateSelect(selectedDateTime);
    } else {
      onInvalidDate(dateInputStatus, selectedDateTime);
    }
  }, [
    excludeDates,
    getSelectedDateWithTime,
    includeDates,
    maxDate,
    minDate,
    onDateSelect,
    onInvalidDate,
    selectedDate,
    state.preSelectionDate,
    withTime,
  ]);

  const keyDownHandlers: { [key: string]: any } = useMemo(
    () => ({
      ArrowDown(event: KeyboardEvent) {
        event.preventDefault();
        const preSelectionDate = addWeeks(state.preSelectionDate, 1);
        const isDisabled = isDayDisabled(
          preSelectionDate,
          minDate as Date,
          maxDate as Date,
          excludeDates as Date[],
          includeDates as Date[]
        );
        !isDisabled && dispatch(setPreSelectionDate(preSelectionDate));
      },
      ArrowUp(event: KeyboardEvent) {
        event.preventDefault();
        const preSelectionDate = subWeeks(state.preSelectionDate, 1);
        const isDisabled = isDayDisabled(
          preSelectionDate,
          minDate as Date,
          maxDate as Date,
          excludeDates as Date[],
          includeDates as Date[]
        );
        !isDisabled && dispatch(setPreSelectionDate(preSelectionDate));
      },
      ArrowLeft(event: KeyboardEvent) {
        event.preventDefault();
        const preSelectionDate = subDays(state.preSelectionDate, 1);
        const isDisabled = isDayDisabled(
          preSelectionDate,
          minDate as Date,
          maxDate as Date,
          excludeDates as Date[],
          includeDates as Date[]
        );
        !isDisabled && dispatch(setPreSelectionDate(preSelectionDate));
      },
      ArrowRight(event: KeyboardEvent) {
        event.preventDefault();
        const preSelectionDate = addDays(state.preSelectionDate, 1);
        const isDisabled = isDayDisabled(
          preSelectionDate,
          minDate as Date,
          maxDate as Date,
          excludeDates as Date[],
          includeDates as Date[]
        );
        !isDisabled && dispatch(setPreSelectionDate(preSelectionDate));
      },
      Home(event: KeyboardEvent) {
        event.preventDefault();
        const preSelectionDate = startOfWeek(state.preSelectionDate);
        const isDisabled = isDayDisabled(
          preSelectionDate,
          minDate as Date,
          maxDate as Date,
          excludeDates as Date[],
          includeDates as Date[]
        );
        !isDisabled && dispatch(setPreSelectionDate(preSelectionDate));
      },
      End(event: KeyboardEvent) {
        event.preventDefault();
        const preSelectionDate = endOfWeek(state.preSelectionDate);
        const isDisabled = isDayDisabled(
          preSelectionDate,
          minDate as Date,
          maxDate as Date,
          excludeDates as Date[],
          includeDates as Date[]
        );
        !isDisabled && dispatch(setPreSelectionDate(preSelectionDate));
      },
      Escape() {
        inputRef.current?.focus();
        if (selectedDate) {
          dispatch(setPreSelectionDate(selectedDate as Date));
        }
        dispatch(setCalendarOpenState(false));
      },
      PageUp(event: KeyboardEvent) {
        event.preventDefault();
        let preSelectionDate;
        if (event.shiftKey) {
          preSelectionDate = subYears(state.preSelectionDate, 1);
        } else {
          preSelectionDate = subMonths(state.preSelectionDate, 1);
        }
        const isDisabled = isDayDisabled(
          preSelectionDate,
          minDate as Date,
          maxDate as Date,
          excludeDates as Date[],
          includeDates as Date[]
        );
        !isDisabled && dispatch(setPreSelectionDate(preSelectionDate));
      },
      PageDown(event: KeyboardEvent) {
        event.preventDefault();
        let preSelectionDate;
        if (event.shiftKey) {
          preSelectionDate = addYears(state.preSelectionDate, 1);
        } else {
          preSelectionDate = addMonths(state.preSelectionDate, 1);
        }
        const isDisabled = isDayDisabled(
          preSelectionDate,
          minDate as Date,
          maxDate as Date,
          excludeDates as Date[],
          includeDates as Date[]
        );
        !isDisabled && dispatch(setPreSelectionDate(preSelectionDate));
      },
      Enter(event: KeyboardEvent) {
        event.preventDefault();
        onDateSelectHandler();
      },
    }),
    [excludeDates, includeDates, maxDate, minDate, onDateSelectHandler, selectedDate, state.preSelectionDate]
  );

  /*-------------------------Getter Props Start------------------------ */

  const getInputProps = useCallback(
    ({ onFocus, onKeyDown, ...rest }: GetInputPropsOptions = {}) => {
      return {
        inputRef: inputRef,
        onFocus: (event: FocusEvent<HTMLInputElement>) => {
          onFocus && onFocus(event);
        },
        onClick: () => {
          state.open === false ? openCalendar() : closeCalendar();
        },
        value: state.inputValue !== null ? state.inputValue : selectedDate ? format(selectedDate, dateFormat) : "",
        onKeyDown: (event: Event) => {
          onKeyDown?.(event as any);
          const key = normalizeArrowKey(event as KeyboardEvent);
          if (key && keyDownHandlers[key]) {
            keyDownHandlers[key](event);
          }
        },
        onChange: (event: ChangeEvent<HTMLInputElement>, value: string) => {
          if (typeof event?.target.value === "string") {
            debounceFn(() => {
              try {
                let selectedDateTime = parse(value, dateFormat, new Date());
                const validDate = isValid(selectedDateTime);
                if (!isSameDay(selectedDateTime, selectedDate as Date)) {
                  dispatch(setInputValue(value));

                  if (validDate) {
                    dispatch(setPreSelectionDate(selectedDateTime, false));
                    if (withTime) {
                      selectedDateTime = getSelectedDateWithTime(selectedDate, selectedDateTime);
                    }
                    const dateInputStatus = isValidDate(selectedDateTime, minDate, maxDate, includeDates, excludeDates);
                    if (dateInputStatus === true) {
                      onDateSelect(selectedDateTime);
                    } else {
                      onInvalidDate && onInvalidDate(dateInputStatus, selectedDateTime);
                    }
                  } else {
                    dispatch(setPreSelectionDate(new Date(), false));
                    onDateSelect(null);
                  }
                }
              } catch (e) {}
            }, TIME_CHANGE_DELAY);
          }
        },
        ...rest,
      };
    },
    [
      dateFormat,
      debounceFn,
      excludeDates,
      getSelectedDateWithTime,
      includeDates,
      keyDownHandlers,
      maxDate,
      minDate,
      onDateSelect,
      onInvalidDate,
      selectedDate,
      state.inputValue,
      withTime,
      openCalendar,
      closeCalendar,
      state.open,
    ]
  );

  const getCalendarRootProps = useCallback(
    (options?: React.HTMLProps<HTMLDivElement>) => {
      return {
        role: "dialog",
        "aria-modal": true,
        "aria-labelledby": dialogLabelId,
        ...options,
      };
    },
    [dialogLabelId]
  );

  const getGridProps = useCallback(
    (options?: React.HTMLProps<HTMLTableElement>) => {
      return {
        id: `grid-${dialogLabelId}`,
        role: "grid",
        "aria-labelledby": dialogLabelId,
        ...options,
      };
    },
    [dialogLabelId]
  );

  const getPreviousYearProps = useCallback(
    (options?: GetButtonPropsOptions) => {
      return {
        onClick: () => dispatch(setPreviousYear()),
        disabled: yearDisabledBefore(state.preSelectionDate, minDate as Date, includeDates as Date[]),
        ...options,
      };
    },
    [includeDates, minDate, state.preSelectionDate]
  );

  const getPreviousMonthProps = useCallback(
    (options?: GetButtonPropsOptions) => {
      return {
        onClick: () => dispatch(setPreviousMonth(showMonths)),
        disabled: monthDisabledBefore(state.preSelectionDate, minDate as Date, includeDates as Date[]),
        ...options,
      };
    },
    [includeDates, minDate, showMonths, state.preSelectionDate]
  );

  const getMonthTitleProps = useCallback(
    (options?: React.HTMLProps<HTMLHeadingElement>) => {
      return {
        id: dialogLabelId,
        "aria-live": "polite",
        ...options,
      };
    },
    [dialogLabelId]
  );

  const getNextMonthProps = useCallback(
    (options?: GetButtonPropsOptions) => {
      return {
        onClick: () => dispatch(setNextMonth(showMonths)),
        disabled: monthDisabledAfter(state.preSelectionDate, maxDate as Date, includeDates as Date[]),
        ...options,
      };
    },
    [includeDates, maxDate, showMonths, state.preSelectionDate]
  );

  const getNextYearProps = useCallback(
    (options?: GetButtonPropsOptions) => {
      return {
        onClick: () => dispatch(setNextYear()),
        disabled: yearDisabledAfter(state.preSelectionDate, maxDate as Date, includeDates as Date[]),
        ...options,
      };
    },
    [includeDates, maxDate, state.preSelectionDate]
  );

  const getDayProps = useCallback(
    (day: Day, options?: GetButtonPropsOptions) => {
      const { onClick, ...restOptions } = options || {};
      const selectedAttributes = day.preSelected
        ? {
            tabIndex: 0,
            "aria-selected": true,
          }
        : {
            tabIndex: -1,
          };
      return {
        ref: handleRefs((dayNode: any) => {
          if (day.belongToMonth) {
            const dayKey: string = format(day.date, DATE_INTERNAL_FORMAT);
            daysRef.current[dayKey] = dayNode;
          }
        }),
        onClick: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
          dispatch(setCalendarOpenState(false));
          dispatch(setPreSelectionDate(day.date));
          dispatch(setRangeHoverDay(null));
          let selectedDateTime = day.date;
          if (withTime) {
            selectedDateTime = getSelectedDateWithTime(selectedDate, selectedDateTime);
          }
          onDateSelect(selectedDateTime);
          onClick?.(event);
        },
        onMouseEnter: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
          if (!day.disabled && (selectsStart || selectsEnd)) {
            dispatch(setRangeHoverDay(day.date));
          }
        },
        ...selectedAttributes,
        ...restOptions,
      };
    },
    [withTime, onDateSelect, getSelectedDateWithTime, selectedDate, selectsStart, selectsEnd]
  );

  /*-------------------------Getter Props End------------------------ */

  const nextMonth = useCallback(() => {
    dispatch(setNextMonth(showMonths));
  }, [showMonths]);

  const prevMonth = useCallback(() => {
    dispatch(setPreviousMonth(showMonths));
  }, [showMonths]);
  const nextYear = useCallback(() => {
    dispatch(setNextYear());
  }, []);
  const prevYear = useCallback(() => {
    dispatch(setPreviousYear());
  }, []);

  const selectTime = useCallback(
    (time: Date | null) => {
      if (!time && !selectedDate) {
        onDateSelect(null);
      } else {
        const baseDate = onlyTime || !selectedDate ? new Date() : (selectedDate as Date);
        const seconds = time ? getSeconds(time) : 0;
        const minutes = time ? getMinutes(time) : 0;
        const hours = time ? getHours(time) : 0;
        const selectedTime = setHours(setMinutes(setSeconds(baseDate, seconds), minutes), hours);
        onDateSelect(selectedTime);
      }
    },
    [onDateSelect, onlyTime, selectedDate]
  );

  return {
    // prop getters.
    getInputProps,
    getCalendarRootProps,
    getGridProps,
    getPreviousYearProps,
    getPreviousMonthProps,
    getMonthTitleProps,
    getNextMonthProps,
    getNextYearProps,
    getDayProps,
    // actions.
    nextMonth,
    prevMonth,
    nextYear,
    prevYear,
    openCalendar,
    closeCalendar,
    selectTime,

    // state.
    isOpen: state.open,
    months,
    times,
  };
};
